/* eslint-disable no-underscore-dangle */
/* eslint-disable max-classes-per-file */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import { Component, Inject } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonSize } from 'src/aero/aero-button/aero-button.component';
import Recorder from 'recorder-js';
import { AnalyticsService } from '../../../common/analytics.service';
import { AppConfigService } from '../../../common/services/app-config/app-config.service';
import { AudioCompatibilityService } from '../audio-compatibility.service';
import { AudioVisualService } from '../audio-visual/audio-visual.service';
import { BULB_BUSY_COVER_SERVICE } from '../../angularjs.providers';
import { BulbFetchMediaDataService } from '../fetch-media-data.service';
import 'jquery';
import { UploadAssetService } from '../../../common/upload-asset.service';

/**
 * Component for audio recording.
 */
@Component({
  selector: 'audio-record',
  templateUrl: './audio-record.component.html',
  standalone: false,
})
export class AudioRecordComponent {
  /**
   * Audio Context.
   */
  private readonly audioContext = new (window.AudioContext || window.webkitAudioContext)();

  /**
   * Static reference to the (window) URL object.
   */
  private static readonly URL = (window.URL || window.webkitURL);

  /**
   * The audio object URL generated to preview the audio playback.
   */
  private audioBlobObjectURL : string;

  /**
   * Object URL.
   */
  public blobSrc : SafeUrl;

  /**
   * Time limit on the amount the user could record.
   */
  public exceededRecordingLimit : boolean = false;

  /**
   * Limit imposed on the amount a user can record, which is 2 hours.
   */
  private static readonly RECORDING_LIMIT : number = 2;

  /**
   * The time difference during the interval check to send to the playback component for display.
   */
  public recordingTimeDifference : number;

  /**
   * Recording Interval constant.
   */
  private static RECORDING_INTERVAL_CHECK : number = 3600000;

  /**
   * Boolean indicating if the user is recording.
   */
  public isRecording : boolean = false;

  /**
   * Constant for audio pulse countdown. We make it 4 seconds so that it starts recording after the 3 second recording
   * pulse.
   */
  private readonly AUDIO_COUNTDOWN_TIMER = 4000;

  /**
   * Boolean indicating if the user has finished recording audio.
   */
  public doneRecording : boolean = false;

  /**
   * Wavesurfer instance that will visualize the audio waveform during recording.
   */
  private waveSurferInstance = null;

  /**
   * Media recorder instance that will be used to capture the data. ( An instance of Recorder.js ).
   */
  private mediaRecorder = new Recorder(this.audioContext);

  /**
   * Current recording media stream.
   */
  private mediaStream : MediaStream = null;

  /**
   * Audio Blob that will hold the RAW audio data that has been captured.
   * Media recorder instance that will be used to capture the data. ( An instance of Recorder.js ).
   */
  private audioBlob : Blob = null;

  /**
   * Interval function reference.
   */
  private timeInterval : any = null;

  /**
   * Indication of prod vs test environment.  Using lowercase as env.name may be injected as all lowercase.
   */
  private runningOnTestEnv: boolean = this.AppConfigService.config.env.name.toLowerCase() !== 'production';

  /**
   * Button size of aero button
   */
  public buttonSize: ButtonSize = ButtonSize.LARGE;

  /**
   * Default Constructor.
   *
   * @param BulbAssetLibraryService
   * @param BulbAuthService
   * @param BulbBrowserService
   * @param BulbBusyCoverService
   * @param AppConfigService
   * @param dynamicDialogRef
   * @param dialogConfig
   * @param sanitizer
   * @param $translate
   * @param fetchMediaService
   * @param analyticsService
   * @param audioCompatibilityService
   * @param uploadAssetService
   * @param waveSurferService
   */
  constructor(
    @Inject('BulbAssetLibraryService') private BulbAssetLibraryService,
    @Inject('BulbAuthService') private BulbAuthService,
    @Inject('BulbBrowserService') private BulbBrowserService,
    @Inject(BULB_BUSY_COVER_SERVICE) private BulbBusyCoverService,
    private AppConfigService: AppConfigService,
    public dynamicDialogRef: DynamicDialogRef,
    public dialogConfig: DynamicDialogConfig,
    private sanitizer : DomSanitizer,
    @Inject('$translate') private $translate,
    private fetchMediaService: BulbFetchMediaDataService,
    private analyticsService : AnalyticsService,
    private audioCompatibilityService : AudioCompatibilityService,
    private uploadAssetService : UploadAssetService,
    private waveSurferService : AudioVisualService,
  ) {
  }

  /**
   * Start Audio Recording.
   */
  startRecording(): void {
    // Interactively request access to the user's media, i.e audio device(s)
    this.fetchMediaService.fetchMedia({ audio: true }).then((stream) => {
      this.mediaStream = stream;
      this._showAudioRecordingToast();
      const audioCaptureRecordingEventObject : any = {
        event: 'AudioCaptureRecordingUser',
        userId: this.BulbAuthService.currentUser.id,
      };
      this.analyticsService.pushEvent(audioCaptureRecordingEventObject);
      // Start countdown:
      new AudioRecordComponent.CountDown();
      // Create wavesurfer instance to visual audio input in the form of an audio wave:
      this._createWaveSurferInstance();
      setTimeout(() => {
        this._initializeMicrophone();
        this._captureAudioStream(stream);
        this._initializeRecordingWatcher();
      }, this.AUDIO_COUNTDOWN_TIMER);
    }).catch((error) => {
      console.log('Couldn\'t access the user\'s media data: ', error);
    });
  }

  /**
   * Stop Audio Recording.
   */
  stopRecording() : void {
    this.isRecording = false;
    this.doneRecording = true;
    // Stop the MediaRecorder object form recording data:
    this.mediaRecorder.stop().then(({ blob, buffer }) => {
      this.audioBlob = blob;
      // By default, Angular WILL NOT TRUST this "blob:" style URLs. However, since we
      // know these are going to be expected, we can use the DOM Sanitizer to bypass
      // the security checks on these images.
      // NOTE: The sanitizer doesn't return Strings - it returns SafeUrls.
      this.audioBlobObjectURL = URL.createObjectURL(this.audioBlob);
      this.blobSrc = this.sanitizer.bypassSecurityTrustUrl(this.audioBlobObjectURL);
    });
    clearInterval(this.timeInterval);
    this._dereferenceMediaStream();
    this._destroyWaveSurferInstance();
  }

  /**
   * Close the Audio Recording Dialog.
   */
  closeAudioDialog() : void {
    clearInterval(this.timeInterval);
    this._destroyWaveSurferInstance();
    this.dynamicDialogRef.close();
  }

  /**
   * Close the Audio Recording Dialog if enter pressed
   * @param event
   */
  closeAudioDialogKeyPress(event) : void {
    this.BulbAssetLibraryService.dialog.closeCurrentModalKeyPress(event);
    this.closeAudioDialog();
  }

  /**
   * 'Delete' the audio recording that was just taken and redirect the user to the initial audio recording state.
   */
  handleDeleteAudioRecording(deleteRecording : boolean) : void {
    // Set appropriate variables:
    if (deleteRecording) {
      this.isRecording = false;
      this.doneRecording = false;
      // Revoke created Object URL:
      this._revokeGeneratedAudioObjectURL();
    }
  }

  /**
   * 'Download' the audio recording to the users local machine.
   * @param audioRecordingFileName
   */
  handleAudioRecordingDownload(audioRecordingFileName : string) {
    // On ***Legacy*** Edge, in order to record we have to create a dummy link in order to download the audio asset
    // Since Recorderjs's 'download' function doesn't work on MS Edge.
    if (this.audioCompatibilityService.isLegacyEdgeyBrowser()) {
      const link = document.createElement('a');
      const audioObjectUrl : string = this.audioBlobObjectURL.replace('unsafe:', '');
      link.setAttribute('href', audioObjectUrl);
      link.setAttribute('download', audioRecordingFileName);
      document.body.appendChild(link);
      // onClick property
      link.click();
      document.body.removeChild(link);
    } else {
      Recorder.download(this.audioBlob, audioRecordingFileName);
    }
  }

  /**
   * Watcher function that keeps an eye on how long the user has been recording for.
   *
   * @private
   */
  _initializeRecordingWatcher() : void {
    const date = new Date();
    if (this.runningOnTestEnv) {
      AudioRecordComponent.RECORDING_INTERVAL_CHECK = 6000;
    }
    this.timeInterval = setInterval(() => {
      this._checkInterval(date);
    }, AudioRecordComponent.RECORDING_INTERVAL_CHECK);
  }

  /**
   * Interval function that checks every hour.
   *
   * @param date
   * @private
   */
  _checkInterval(date) : void {
    const futureDate = new Date();
    const futureDateTime : number = (this.runningOnTestEnv) ? futureDate.getMinutes() : futureDate.getHours();
    const dateTime : number = (this.runningOnTestEnv) ? date.getMinutes() : date.getHours();
    const timeDifference : number = (futureDateTime - dateTime);
    if (timeDifference === AudioRecordComponent.RECORDING_LIMIT) {
      this.recordingTimeDifference = (this.runningOnTestEnv) ? (timeDifference / 60) : timeDifference;
      clearInterval(this.timeInterval);
      this.exceededRecordingLimit = true;
      this.stopRecording();
    }
  }

  /**
   * Helper method to show the audio recording toast.
   *
   * @private
   */
  _showAudioRecordingToast() : void {
    const audioRecordingMessage = this.$translate.instant('pageview_audio_recording_toast');
    window.iziToast.show({
      theme: 'dark',
      timeout: 4000,
      close: false,
      icon: 'fas fa-microphone',
      message: audioRecordingMessage,
      position: 'topCenter',
      progressBarColor: 'rgb(0,174,239)', // $bulb-blue-color
    });
  }

  /**
   * Convert blob to File object.
   *
   * @param theBlob
   * @param fileName
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  _blobToFile(theBlob : Blob, fileName : string) : File {
    const b: any = theBlob;
    // A Blob() is almost a File() - it's just missing the two properties below which we will add
    b.lastModifiedDate = new Date();
    b.name = fileName;

    // Cast to a File() type
    return <File>theBlob;
  }

  /**
   * Helper function to revoke the generated object ( audio ) url.
   *
   * @private
   */
  _revokeGeneratedAudioObjectURL() : void {
    if (this.audioBlobObjectURL) {
      // Revoke created Object URL:
      AudioRecordComponent.URL.revokeObjectURL(this.audioBlobObjectURL);
    }
  }

  /**
   * 'Save' the audio recording sending it to the server to transcode.
   * @param audioData
   */
  handleAudioRecordingSave(audioData : any) : void {
    if (audioData.save) {
      this._saveAudioRecording(audioData);
    }
  }

  /**
   * Helper method to save the audio by sending it to the server for transcoding.
   *
   * @param audioData
   * @private
   */
  private _saveAudioRecording(audioData : any) : void {
    // Grab the boolean value from the injected aero dynamic-dialog config object indicating whether or not we are
    // this modal is being opened from the assetlibrary or not.
    // @ts-ignore
    const { fromWithinAssetLibrary } = this.dialogConfig;

    this.closeAudioDialog();
    this.BulbAssetLibraryService.dialog.dismissAllModals();

    // Convert the blob to a file to send to the server:
    const audioFile = this._blobToFile(this.audioBlob, audioData.fileName);
    // Update the file and create the asset.
    this.uploadAssetService.uploadFileWithoutBrowser(
      audioFile,
      'audio/*',
      fromWithinAssetLibrary,
      (!fromWithinAssetLibrary) ? 'page' : 'assetlibrary',
    );
  }

  /**
   * Helper method to instantiate the wavesurfer instance.
   *
   * @private
   */
  private _createWaveSurferInstance() : void {
    this.waveSurferInstance = this.waveSurferService.createWaveFormInstance('#audio-recording-microphone-waveform');
    jQuery('wave').css({ position: '', display: '' });
  }

  /**
   * Helper method to init the recording stream.
   *
   * @private
   */
  private _initializeMicrophone() : void {
    this.isRecording = true;
    setTimeout(() => {
      jQuery('.audio-button-stop').hide().fadeIn('slow').focus();
    });
    // Start the Microphone to detect input data:
    // Causes an issue on iOS Safari, can't instantiate the microphone. It has to do with multiple requests to
    // gUM.
    if (!this.BulbBrowserService.isIOSMobile()) {
      this.waveSurferInstance.microphone.start();
    }
  }

  /**
   * Capture the audio stream coming from the user.
   *
   * @param stream Stream of media content. In this case, audio.
   * @private
   */
  private _captureAudioStream(stream : MediaStream) : void {
    this.mediaRecorder.init(stream);
    this.mediaRecorder.start();
  }

  /**
   * Dereference the granted media stream from the user.
   *
   * @private
   */
  private _dereferenceMediaStream() : void {
    if (!this.mediaStream) return;
    this.mediaStream.getAudioTracks().forEach((track) => {
      track.stop();
    });
    // Dereference the media stream:
    this.mediaStream = null;
  }

  /**
   * Destroy the Wavesurfer instance with the associated Mircrophone plugin:
   *
   * @private
   */
  private _destroyWaveSurferInstance(): void {
    if (this.waveSurferInstance) {
      // Destroy wavesurfer instance, releasing all events and objects:
      this.waveSurferInstance.destroy();
      // Stop visualization and disconnect microphone:
      this.waveSurferInstance.microphone.stopDevice();
      // Destroy the Microphone plugin:
      this.waveSurferInstance.microphone.destroy();
    }
  }

  /**
   * Inner Class to handle displaying the countdown 3...2...1
   */
  static CountDown = class {
    /**
     * Starting countdown number.
     */
    private count : any = 3;

    /**
     * Reference to the HTML DOM countdown element.
     */
    private $htmlCountDownElement = jQuery('#count-down');

    /**
     * Reference to the HTML DOM audio button 'start' element.
     */
    private $audioButtonStart = jQuery('.audio-button-start');

    /**
     * Count down delay in milliseconds.
     */
    private readonly COUNT_DOWN_DELAY = 1000;

    /**
     * Function to run countdown.
     */
    public timerFunction: any = setInterval(() => { this._handleTimer(); }, this.COUNT_DOWN_DELAY);

    /**
     * Logic to end the countdown.
     *
     * @private
     */
    private _endCountdown() {
      clearInterval(this.timerFunction);
      this.$htmlCountDownElement.hide();
      setTimeout(() => {
        jQuery('.audio-button-stop').addClass('audio-pulse');
      }, 500);
    }

    /**
     * Handle the countdown.
     *
     * @private
     */
    private _handleTimer() {
      this.$htmlCountDownElement.show();
      // Add a class to the 'start' button indicating to the user that audio recording is about to start:
      this.$audioButtonStart.addClass('audio-pulse');
      this.$audioButtonStart.html('');
      if (this.count === 0) {
        this._endCountdown();
      } else {
        this.$htmlCountDownElement.hide().html(this.count).fadeIn('slow');
        // eslint-disable-next-line no-plusplus
        this.count--;
      }
    }
  };
}
